import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { quickbaseFetch } from './Utils/Functions/QbAuthedFetch';
import { initializeAndFetchAllTables } from './Utils/Functions/Dexie-Setup';
import { getCookieValue, pFetch, ticketCookieName } from './Utils/Functions/helpers';
import { parseXml } from './Utils/Functions/helpers';
import { db } from './Utils/Functions/Dexie-Setup';
import { useLiveQuery } from 'dexie-react-hooks';
import { appId } from './Utils/Functions/Table_Configs';
import { translations } from './Utils/Maps/translations';

const tabUid = `${Date.now()}-${Math.random()}`

const StoreContext = createContext();
function getLang() {
  if (navigator.languages !== undefined)
    return navigator.languages[0];
  return navigator.language;
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const hoursNum = Number(window.localStorage.getItem("hours") || 504);
// Create a provider component
export const StoreProvider = ({ children, iframeSrc, userAgent }) => {
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);
  const [isAuthed, setIsAuthed] = useState(!!getCookieValue(ticketCookieName));
  const [language, stateSetLanguage] = useState(window.localStorage.getItem("language") || (getLang().includes("es") ? "es" : "en"));

  const [translation, setTranslation] = useState(translations[language]);

  const [hours, stateSetHours] = useState(Number.isNaN(hoursNum) ? 72 : hoursNum);
  const [settingsVisited, stateSetSettingsVisited] = useState(window.localStorage.getItem("settingsVisited") === "true");
  const pendingSubmissions = useLiveQuery(() => db.dailyFieldReports.where("recordId").below(0).and(dfr => (dfr?.submitted)).primaryKeys());
  const debounce = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isReconciling = useRef(false);

  const { fetchAndStoreTableData, fetchAndStoreAllTables, postRecords, getTableUpdates, checkAndSyncTables, clearData, syncIfTimePassed, clearFetchedData, reconcileLineItems } =
    useMemo(() => initializeAndFetchAllTables(new URL(iframeSrc).origin, userAgent, quickbaseFetch), [iframeSrc, userAgent]);

  const setLanguage = useCallback((value) => {
    stateSetLanguage(value);
    window.localStorage.setItem("language", String(value))
  }, [stateSetLanguage]);

  const setHours = useCallback((value) => {
    let newHours = Number(value);
    newHours = Number.isNaN(newHours) ? 0 : newHours;
    stateSetHours(newHours);
    window.localStorage.setItem("hours", String(value));
    const ticket = getCookieValue(ticketCookieName);
    if (ticket && isOnline) {
      clearTimeout(debounce.current);
      debounce.current = setTimeout(() => signIn("", "", ticket, newHours), 10000);
    }
  }, [stateSetHours]);

  const setSettingsVisited = useCallback((value) => {
    stateSetLanguage(value);
    window.localStorage.setItem("settingsVisited", String(value))
  })
  const signOut = useCallback(() => {
    document.cookie = ticketCookieName + "=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;";
    clearTimeout(debounce.current);
    setIsAuthed(false);
    clearFetchedData();
  }, [setIsAuthed]);


  const signIn = useCallback(async (username, password, ticket, manualHours) => {
    const req = await pFetch(`https://atlas-titan.quickbase.com/db/main`, {
      method: "POST",
      headers: {
        "Content-Type": "application/xml",
        "QUICKBASE-ACTION": "API_Authenticate"
      },
      body: `
      <qdbapi>
          ${!ticket ? `<username>${username}</username>` : ""}
          ${!ticket ? `<password>${password}</password>` : ""}
          ${ticket ? `<ticket>${ticket}</ticket>` : ""}
          <hours>${(manualHours ?? hours) || 200}</hours>
      </qdbapi>
      `
    })

    const res = await req.text();
    const resObj = parseXml(res);
    if (req.ok) {
      const ticket = resObj?.qdbapi?.ticket?.["#text"];

      if (ticket) {
        const maxAge = manualHours ?? hours ? `; max-age=${Math.ceil((hours * 60) * 60)}` : "";
        const cookie = `${ticketCookieName}=${ticket}${maxAge}; path=/`;
        window.document.cookie = cookie;
        setIsAuthed(true);
        return { ok: true, body: res, parsedBody: resObj }
      } else {
        setIsAuthed(false);
        return { ok: false, body: res, parsedBody: resObj }
      }
    } else {
      setIsAuthed(false);
      return { ok: false, body: res, parsedBody: resObj }
    }
  }, [setIsAuthed, hours]);


  useEffect(() => {
    const reconcileLineItemsWrapper = async () => {
      if (isReconciling.current) return;

      try {
        isReconciling.current = true;
        await reconcileLineItems();
      } finally {
        isReconciling.current = false;
      }
    };

    if (isOnline && isAuthed) {
      try {
        syncIfTimePassed();
        reconcileLineItemsWrapper(); // Call it initially
        const ticket = getCookieValue(ticketCookieName);
        signIn("", "", ticket);
        // fetchAndStoreAllTables()
        const interval = setInterval(() => {
          syncIfTimePassed();
          reconcileLineItemsWrapper(); // Call it periodically
        }, 1000000);
        return () => {
          clearInterval(interval);
        }
      } catch (error) {
        console.error(error)
      }
    }
  }, [isOnline, isAuthed, syncIfTimePassed, reconcileLineItems])

  useEffect(() => {

    window.localStorage.setItem(`${appId}_IsSubmitting`, "false");
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false);


    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

    };
  }, []);

  useEffect(() => {
    // alert(`${isOnline} ${isAuthed} ${!!getCookieValue(ticketCookieName)} ${!!pendingSubmissions?.length}  ${!submitting.current}`)
    if (isOnline && isAuthed && !!getCookieValue(ticketCookieName) && pendingSubmissions?.length && (window.localStorage.getItem(`${appId}_IsSubmitting`) === "false")) {
      (async () => {
        if (window.localStorage.getItem(`${appId}_IsSubmitting`) !== "false") return;

        window.localStorage.setItem(`${appId}_IsSubmitting`, tabUid);
        
        await delay(5);
        
        if (window.localStorage.getItem(`${appId}_IsSubmitting`) !== tabUid) {
          setIsSubmitting(false); // Hide loading overlay
          return;
        }
        
        setIsSubmitting(true); // Show loading overlay
        const beforeUnloadHandler = (e) => e.preventDefault();
        window.addEventListener("beforeunload", beforeUnloadHandler);
        
        const pendingIds = await db.dailyFieldReports.where("recordId").below(0).and(dfr => (dfr?.submitted)).primaryKeys();
        const newIds = await postRecords("dailyFieldReports", pendingIds, 1);

        window.removeEventListener("beforeunload", beforeUnloadHandler);
        window.localStorage.setItem(`${appId}_IsSubmitting`, "false");

        setIsSubmitting(false); // Hide loading overlay
      })();
    }
  }, [isOnline, isAuthed, pendingSubmissions])




  useEffect(() => setTranslation(translations[language]), [language])


  return (
    <StoreContext.Provider
      value={{
        isAuthed,
        isOnline,
        isSubmitting,
        signIn,
        setIsAuthed,
        translation,
        language,
        isSpanish: language === "es",
        hours,
        setLanguage,
        setHours,
        setSettingsVisited,
        signOut,
        fetchAndStoreTableData,
        fetchAndStoreAllTables,
        postRecords,
        getTableUpdates,
        checkAndSyncTables,
        clearData,
        syncIfTimePassed,
        reconcileLineItems
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

// Custom hook to use the store context
export const useStore = () => useContext(StoreContext);